import {
  EntitySearchReferenceEntity,
  WbsItemAdditionalPropertyEntity,
  WbsItemAdditionalPropertyType,
} from '../../../../../../../../domain/entity/WbsItemAdditionalPropertyEntity'
import { i18nLabelVoService } from '../../../../../../../../domain/value-object/I18nLabelVO'
import { wbsItemAdditionalPropertyValuesVoService } from '../../../../../../../../domain/value-object/WbsItemAdditionalPropertyValuesVO'
import SearchOptions from '../../../../../../../../utils/searchOptions'
import {
  BooleanValueGetter,
  CellDef,
  DateTermValueGetter,
  DateTimeValueGetter,
  DateValueGetter,
  EntitySearchCellDef,
  MultiEntitySearchCellDef,
  MultiLineTextValueGetter,
  MultiReferencedEntityValueGetter,
  MultiSelectCellDef,
  MultiSelectOptionValueGetter,
  NumberValueGetter,
  ReferencedEntityValueGetter,
  SelectCellDef,
  SelectOptionValueGetter,
  TextValueGetter,
} from '../../../../../../../containers/SingleSheetV2/table/model'
import { GetEntitySearchReferenceEntityRepository } from '../../../../../../../hooks/useWbsItemAdditionalProperties'

export const toCellDef = (
  wbsItemAdditionalProperty: WbsItemAdditionalPropertyEntity,
  getEntitySearchReferenceEntityRepository: GetEntitySearchReferenceEntityRepository
): CellDef => {
  const propertyType = wbsItemAdditionalProperty.propertyType
  switch (propertyType) {
    case WbsItemAdditionalPropertyType.NUMBER:
    case WbsItemAdditionalPropertyType.TEXT:
    case WbsItemAdditionalPropertyType.MULTI_LINE_TEXT:
    case WbsItemAdditionalPropertyType.DATE:
    case WbsItemAdditionalPropertyType.DATE_TIME:
    case WbsItemAdditionalPropertyType.CHECKBOX:
      return toCommonCellDef(propertyType, wbsItemAdditionalProperty)
    case WbsItemAdditionalPropertyType.SELECT:
    case WbsItemAdditionalPropertyType.MULTI_SELECT:
      return toSelectCellDef(propertyType, wbsItemAdditionalProperty)
    case WbsItemAdditionalPropertyType.ENTITY_SEARCH:
      return toEntitySearchCellDef(
        propertyType,
        wbsItemAdditionalProperty,
        getEntitySearchReferenceEntityRepository
      )
    default:
      throw new Error(
        `Unsupported wbs item additional property type(=${propertyType}). uuid=${wbsItemAdditionalProperty.uuid}`
      )
  }
}

// Private functions
const toCommonCellDef = (
  propertyType:
    | WbsItemAdditionalPropertyType.NUMBER
    | WbsItemAdditionalPropertyType.TEXT
    | WbsItemAdditionalPropertyType.MULTI_LINE_TEXT
    | WbsItemAdditionalPropertyType.DATE
    | WbsItemAdditionalPropertyType.DATE_TIME
    | WbsItemAdditionalPropertyType.CHECKBOX,
  wbsItemAdditionalProperty: WbsItemAdditionalPropertyEntity
): CellDef => {
  return {
    externalId:
      wbsItemAdditionalProperty.entityExtensionExternalId ||
      wbsItemAdditionalProperty.uuid,
    field: wbsItemAdditionalProperty.uuid,
    label: i18nLabelVoService.getLabel(
      wbsItemAdditionalProperty.propertyNameI18n
    ),
    type: propertyType,
    width: 'half',
    required: wbsItemAdditionalProperty.required,
    editable: true,
    getValue: wbsItemAdditionalPropertyValueGetter(
      wbsItemAdditionalProperty
    ) as
      | NumberValueGetter
      | TextValueGetter
      | MultiLineTextValueGetter
      | DateValueGetter
      | DateTimeValueGetter
      | DateTermValueGetter
      | BooleanValueGetter,
  } as CellDef
}
const toSelectCellDef = (
  propertyType: 'SELECT' | 'MULTI_SELECT',
  wbsItemAdditionalProperty: WbsItemAdditionalPropertyEntity
): SelectCellDef | MultiSelectCellDef => {
  return {
    externalId: wbsItemAdditionalProperty.uuid,
    field: wbsItemAdditionalProperty.uuid,
    label: i18nLabelVoService.getLabel(
      wbsItemAdditionalProperty.propertyNameI18n
    ),
    type: propertyType,
    width: 'half',
    required: wbsItemAdditionalProperty.required,
    editable: true,
    getValue: wbsItemAdditionalPropertyValueGetter(
      wbsItemAdditionalProperty
    ) as SelectOptionValueGetter | MultiSelectOptionValueGetter,
    options: wbsItemAdditionalProperty.selectOptions
      ? wbsItemAdditionalProperty.selectOptions.map(v => ({
          name: v.selectOption,
          value: v.uuid,
        }))
      : [],
    // renderOption: v => <>{v.name}</>, // TODO Background color.
    // renderValue: v => <>{v?.name}</>, // TODO Background color.
  } as SelectCellDef | MultiSelectCellDef
}
const toEntitySearchCellDef = (
  propertyType: WbsItemAdditionalPropertyType.ENTITY_SEARCH,
  wbsItemAdditionalProperty: WbsItemAdditionalPropertyEntity,
  getEntitySearchReferenceEntityRepository: GetEntitySearchReferenceEntityRepository
): EntitySearchCellDef => {
  if (!wbsItemAdditionalProperty.entitySearchReferenceEntity) {
    throw new Error(
      `Unsupported referenceEntity(=${wbsItemAdditionalProperty.entitySearchReferenceEntity}) of wbs item additional property values(uuid=${wbsItemAdditionalProperty.uuid}).`
    )
  }
  const referenceEntityRepository = getEntitySearchReferenceEntityRepository(
    wbsItemAdditionalProperty.entitySearchReferenceEntity,
    wbsItemAdditionalProperty.entitySearchReferenceEntityTicketType
  )
  return {
    externalId: wbsItemAdditionalProperty.uuid,
    field: wbsItemAdditionalProperty.uuid,
    label: i18nLabelVoService.getLabel(
      wbsItemAdditionalProperty.propertyNameI18n
    ),
    type: propertyType,
    width: 'half',
    required: wbsItemAdditionalProperty.required,
    editable: true,
    getValue: wbsItemAdditionalPropertyValueGetter(
      wbsItemAdditionalProperty
    ) as ReferencedEntityValueGetter,
    search: referenceEntityRepository.search,
    displayIcon: false,
  }
}

const wbsItemAdditionalPropertyValueGetter = (
  wbsItemAdditionalProperty: WbsItemAdditionalPropertyEntity
) => {
  return (formModel: object) => {
    const additionalPropertyValues = formModel['additionalPropertyValues']
    if (!additionalPropertyValues) {
      return undefined
    }
    return wbsItemAdditionalPropertyValuesVoService.getValue(
      additionalPropertyValues,
      wbsItemAdditionalProperty.uuid
    )
  }
}
